import apiClient, { extractMessage } from '@/services/apiClient'

export interface ChatMessage {
  role:    'user' | 'assistant'
  content: string
}

export interface ClaudeContext {
  repo:        string
  branch:      string
  activeFile:  string
  /** Raw text currently selected in the Monaco editor — injected into the system prompt. */
  selection?:  string
}

/**
 * POST /api/ide/claude/chat — forward a conversation to the Anthropic API via the PHP gateway.
 *
 * Uses a 120-second timeout because Claude API responses for large files or complex
 * refactoring tasks can legitimately take 30-60 seconds. The default 15-second
 * apiClient timeout is too short and would cause spurious errors.
 *
 * @throws {Error} Human-readable message extracted from the API error body.
 */
export async function sendChatMessage(
  messages: ChatMessage[],
  context:  ClaudeContext,
): Promise<string> {
  try {
    const { data } = await apiClient.post<{ reply: string }>(
      '/api/ide/claude/chat',
      { messages, context },
      { timeout: 120_000 },
    )
    if (!data.reply) throw new Error('Received an empty reply from Claude.')
    return data.reply
  } catch (err) {
    throw new Error(extractMessage(err))
  }
}

/**
 * Extract all <file path="...">...</file> blocks from a Claude reply.
 *
 * Claude wraps proposed file changes in these tags as documented in the system prompt.
 * Each returned object contains the path as given by Claude and the trimmed file content.
 */
export function extractFileBlocks(reply: string): Array<{ path: string; content: string }> {
  const pattern = /<file path="([^"]+)">([\s\S]*?)<\/file>/g
  const blocks:  Array<{ path: string; content: string }> = []
  let match: RegExpExecArray | null

  while ((match = pattern.exec(reply)) !== null) {
    blocks.push({ path: match[1].trim(), content: match[2].trim() })
  }

  return blocks
}

/**
 * Return the message text with <file> blocks collapsed to a single-line summary.
 *
 * This keeps the chat bubble readable when Claude outputs large file contents.
 * The Apply buttons below each message let users apply the full content.
 */
export function collapseFileBlocks(content: string): string {
  return content.replace(
    /<file path="([^"]+)">[\s\S]*?<\/file>/g,
    (_match, path) => `[File ready to apply: ${path}]`,
  )
}
